import React, { useState, useEffect } from 'react';
import {
    Container, Row, Col, Card, Button, Form, Spinner, Alert, Modal
} from 'react-bootstrap';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const API_BASE = 'https://varahibackend.varahiselfdrivecars.com/api/car';

const AdminBannerManager = () => {
    const [banners, setBanners] = useState([]);
    const [images, setImages] = useState([]);
    const [imageLinks, setImageLinks] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedBannerId, setSelectedBannerId] = useState(null);
    const [editImages, setEditImages] = useState([]);
    const [editImageLinks, setEditImageLinks] = useState([]);

    const fetchBanners = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_BASE}/allbanner`);
            setBanners(res.data.banners || []);
        } catch (err) {
            setError('Failed to fetch banners');
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBanners();
    }, []);

    const handleImageChange = (e) => {
        setImages([...e.target.files]);
    };

    const handleLinkChange = (e) => {
        const links = e.target.value.split('\n').filter(link => link.trim() !== '');
        setImageLinks(links);
    };

    const handleUpload = async (e) => {
        e.preventDefault();
        if (images.length === 0 && imageLinks.length === 0) {
            toast.error('Please select images or provide image links.');
            return;
        }
        setUploading(true);
        try {
            const formData = new FormData();
            
            images.forEach((file) => formData.append('images', file));
            
            if (imageLinks.length > 0) {
                formData.append('imageLinks', JSON.stringify(imageLinks));
            }

            await axios.post(`${API_BASE}/bannercreate`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            
            toast.success('Banners uploaded successfully!');
            setImages([]);
            setImageLinks([]);
            setShowAddModal(false);
            fetchBanners();
        } catch (err) {
            toast.error('Failed to upload banners');
            console.error('Upload error:', err);
        } finally {
            setUploading(false);
        }
    };

    const handleEditClick = (bannerId) => {
        setSelectedBannerId(bannerId);
        setEditImages([]);
        setEditImageLinks([]);
        setShowEditModal(true);
    };

    const handleEditImageChange = (e) => {
        setEditImages([...e.target.files]);
    };

    const handleEditLinkChange = (e) => {
        const links = e.target.value.split('\n').filter(link => link.trim() !== '');
        setEditImageLinks(links);
    };

    const handleUpdate = async () => {
        if (!selectedBannerId || (editImages.length === 0 && editImageLinks.length === 0)) {
            toast.error('Please select images or provide image links to update.');
            return;
        }
        try {
            const formData = new FormData();
            
            editImages.forEach((file) => formData.append('images', file));
            
            if (editImageLinks.length > 0) {
                formData.append('imageLinks', JSON.stringify(editImageLinks));
            }

            await axios.put(`${API_BASE}/updatebanner/${selectedBannerId}`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            
            toast.success('Banner updated successfully!');
            setShowEditModal(false);
            fetchBanners();
        } catch (err) {
            toast.error('Failed to update banner');
            console.error('Update error:', err);
        }
    };

    const handleDelete = async (bannerId) => {
        if (!window.confirm('Are you sure you want to delete this banner?')) return;
        try {
            await axios.delete(`${API_BASE}/deletebanner/${bannerId}`);
            toast.success('Banner deleted successfully!');
            fetchBanners();
        } catch (err) {
            toast.error('Failed to delete banner');
        }
    };

    const handleModalClose = () => {
        setShowAddModal(false);
        setImages([]);
        setImageLinks([]);
    };

    return (
        <Container className="py-5">
            <ToastContainer position="top-right" autoClose={2000} />
            
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2 className="mb-0">Admin Banner Management</h2>
                <Button 
                    variant="primary" 
                    onClick={() => setShowAddModal(true)}
                    className="px-4 py-2 rounded-pill"
                >
                    <i className="fas fa-plus me-2"></i>
                    Add Banner
                </Button>
            </div>

            <h4 className="mb-3">Existing Banners</h4>

            {loading ? (
                <div className="text-center">
                    <Spinner animation="border" />
                </div>
            ) : banners.length === 0 ? (
                <Alert variant="info">No banners found.</Alert>
            ) : (
                <div
                    className="d-flex flex-row gap-3 pb-3 overflow-auto overflow-md-visible"
                    style={{
                        flexDirection: window.innerWidth < 768 ? 'column' : 'row',
                        maxHeight: window.innerWidth < 768 ? '500px' : 'none',
                    }}
                >
                    {banners.map((banner) => (
                        <Card key={banner._id} className="shadow-sm flex-shrink-0" style={{ minWidth: '300px' }}>
                            <Card.Body>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <Card.Title className="mb-0">Banner Set</Card.Title>
                                    <div>
                                        <button
                                            className="me-2 btn btn-sm btn-outline-warning"
                                            onClick={() => handleEditClick(banner._id)}
                                        >
                                            <i className="fas fa-edit me-1"></i>
                                        </button>
                                        <button
                                            className="btn btn-sm btn-outline-danger"
                                            onClick={() => handleDelete(banner._id)}
                                        >
                                            <i className="fas fa-trash me-1"></i>
                                        </button>
                                    </div>
                                </div>

                                <div className="d-flex flex-wrap gap-2 mb-3">
                                    {banner.images && banner.images.map((img, idx) => (
                                        <div
                                            key={idx}
                                            className="rounded"
                                            style={{
                                                width: '140px',
                                                height: '80px',
                                                background: `url(${img}) center center / cover no-repeat`,
                                                flexShrink: 0,
                                            }}
                                        />
                                    ))}
                                </div>

                                <p className="text-muted small">
                                    Uploaded: {new Date(banner.createdAt).toLocaleString()}
                                </p>
                            </Card.Body>
                        </Card>
                    ))}
                </div>
            )}

            {/* Add Banner Modal */}
            <Modal show={showAddModal} onHide={handleModalClose} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Add New Banner</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleUpload}>
                        <Form.Group controlId="formFileMultiple" className="mb-3">
                            <Form.Label>
                                <i className="fas fa-image me-2"></i>
                                Upload Images (multiple allowed)
                            </Form.Label>
                            <Form.Control 
                                type="file" 
                                multiple 
                                onChange={handleImageChange}
                                accept="image/*"
                            />
                            <Form.Text className="text-muted">
                                Select one or more image files to upload
                            </Form.Text>
                        </Form.Group>

                        {/* <div className="text-center my-3">
                            <hr className="w-50 mx-auto" />
                            <span className="px-3 bg-white text-muted">OR</span>
                        </div> */}

                        <Form.Group controlId="formImageLinks" className="mb-3">
                            <Form.Label>
                                <i className="fas fa-link me-2"></i>
                                Image Links- Optional
                            </Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                style={{ height: '60px' }}
                                placeholder="Enter Your Link"
                                onChange={handleLinkChange}
                                value={imageLinks.join('\n')}
                            />
                      
                        </Form.Group>

                        <div className="d-flex justify-content-end gap-2 mt-4">
                            <Button 
                                variant="secondary" 
                                onClick={handleModalClose}
                                disabled={uploading}
                            >
                                Cancel
                            </Button>
                            <Button 
                                variant="primary" 
                                type="submit"
                                disabled={uploading || (images.length === 0 && imageLinks.length === 0)}
                            >
                                {uploading ? (
                                    <>
                                        <Spinner animation="border" size="sm" className="me-2" />
                                        Uploading...
                                    </>
                                ) : (
                                    'Upload Banner'
                                )}
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>

            {/* Edit Modal */}
            <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Edit Banner</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group controlId="editImages" className="mb-3">
                            <Form.Label>
                                <i className="fas fa-image me-2"></i>
                                Upload New Images (multiple allowed)
                            </Form.Label>
                            <Form.Control 
                                type="file" 
                                multiple 
                                onChange={handleEditImageChange}
                                accept="image/*"
                            />
                            <Form.Text className="text-muted">
                                Select one or more image files to replace existing ones
                            </Form.Text>
                        </Form.Group>

                        <div className="text-center my-3">
                            <hr className="w-50 mx-auto" />
                            <span className="px-3 bg-white text-muted">OR</span>
                        </div>

                        <Form.Group controlId="editImageLinks" className="mb-3">
                            <Form.Label>
                                <i className="fas fa-link me-2"></i>
                                Image Links- Optional
                            </Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                style={{ height: '60px' }}
                                placeholder="Enter image URLs"
                                onChange={handleEditLinkChange}
                                value={editImageLinks.join('\n')}
                            />
                           
                        </Form.Group>

                        <div className="d-flex justify-content-end gap-2 mt-4">
                            <Button variant="secondary" onClick={() => setShowEditModal(false)}>
                                Cancel
                            </Button>
                            <Button 
                                variant="primary" 
                                onClick={handleUpdate}
                                disabled={editImages.length === 0 && editImageLinks.length === 0}
                            >
                                Update Banner
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>
        </Container>
    );
};

export default AdminBannerManager;